import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import LocationOn from "@mui/icons-material/LocationOn";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CustomLink } from "core/components/CustomLink";
import { useFormatDate } from "core/hooks/useFormatDate";
import { buildQueryString } from "core/utils/buildQueryString";
import Image from "next/legacy/image";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";
import type { IBannerDto, ICategorySummaryDto } from "../../entities";
import { getImageSRC } from "../../utils";

interface EventCardProps {
  id: string;
  title: string;
  blurb: string;
  banner: IBannerDto | undefined;
  articleType: ICategorySummaryDto;
  articleCategory: ICategorySummaryDto | undefined;
  startDate: string;
  endDate: string;
  address?: string | undefined;
  url?: string | undefined;
  isImportant?: boolean;
  onChipClick: () => void;
  onCategoryClick: () => void;
  ref?: Ref<HTMLDivElement>;
}

export function EventCard({
  banner,
  articleType,
  blurb,
  title,
  startDate,
  endDate,
  id,
  address,
  url,
  isImportant,
  articleCategory,
  onChipClick,
  onCategoryClick,
  ref,
}: EventCardProps) {
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:EventCard",
  });
  const formatDate = useFormatDate();
  const startDateFormatted = startDate && formatDate(startDate);
  const endDateFormatted = endDate && formatDate(endDate);
  const href = `/events/${id}`;
  return (
    <Card
      ref={ref}
      sx={{
        display: "flex",
        position: "relative",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {articleType && (
        <Chip
          onClick={onChipClick}
          color="primary"
          label={articleType.title}
          sx={{
            zIndex: 1,
            position: "absolute",
            top: ({ spacing }) => spacing(1),
            right: ({ spacing }) => spacing(1),
          }}
        />
      )}
      <CustomLink href={href} passHref>
        <Box height={250} position="relative" width="100%">
          {banner?.blurDataUrl && banner?.altText && banner?.blobName ? (
            <Image
              src={getImageSRC(banner.blobName)}
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              placeholder="blur"
              alt={banner.altText}
              blurDataURL={banner.blurDataUrl}
              sizes="(max-width: 675px) 100vw, (max-width: 1000px) 45vw, 33vw"
            />
          ) : (
            <Box
              sx={{ height: "100%", width: "100%", backgroundColor: "grey" }}
            />
          )}
        </Box>
        {isImportant && (
          <PriorityHighRoundedIcon
            sx={{
              zIndex: 1,
              position: "absolute",
              top: ({ spacing }) => spacing(1),
              left: ({ spacing }) => spacing(1),
              color: "red",
            }}
          />
        )}
      </CustomLink>

      <CardContent sx={{ flex: 1 }}>
        <Typography
          color="primary"
          sx={{
            "&:hover": { cursor: "pointer" },
            fontWeight: 400,
            fontSize: "0.75rem",
          }}
          onClick={onCategoryClick}
        >
          {articleCategory?.title ?? t("uncategorized")} {">"}
        </Typography>
        <CustomLink href={href} passHref>
          <Typography variant="h5">{title}</Typography>
        </CustomLink>
        <CustomLink
          href={`/api/ics/${title}?${buildQueryString({
            startDate,
            endDate,
            description: blurb,
            location: address ?? url,
          })}`}
          rawHref
          target="_blank"
        >
          <Stack
            my={1}
            spacing={1}
            direction="row"
            color="text.secondary"
            alignItems="flex-start"
          >
            <CalendarMonthIcon fontSize="small" />
            <Typography
              gutterBottom
              variant="body2"
              margin={0}
              color="text.secondary"
            >
              {startDate
                ? t("dateRange", {
                    startDate: startDateFormatted,
                    endDate: endDateFormatted,
                  })
                : startDateFormatted}
            </Typography>
          </Stack>
        </CustomLink>
        {address && (
          <CustomLink
            target="_blank"
            href={`https://www.google.com/maps/search/${encodeURIComponent(
              address
            )}`}
          >
            <Stack my={1} spacing={1} direction="row" alignItems="center">
              <LocationOn fontSize="small" />
              <Typography gutterBottom variant="body2" margin={0}>
                {address}
              </Typography>
            </Stack>
          </CustomLink>
        )}

        {url && (
          <CustomLink href={url} target="_blank">
            <Stack my={1} spacing={1} direction="row" alignItems="flex-start">
              <OpenInNew fontSize="small" />
              <Typography
                gutterBottom
                variant="body2"
                margin={0}
                width="100%"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {url}
              </Typography>
            </Stack>
          </CustomLink>
        )}

        <Typography
          variant="body2"
          sx={{
            mt: 2,
            display: "-webkit-box",
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 4,
          }}
        >
          {blurb}
        </Typography>
      </CardContent>
      <Stack px={3} pb={2} direction="row" justifyContent="flex-end">
        <CustomLink href={href} passHref>
          <Button
            size="small"
            endIcon={<KeyboardDoubleArrowRightIcon sx={{ mb: "2px" }} />}
          >
            {t("readMore")}
          </Button>
        </CustomLink>
      </Stack>
    </Card>
  );
}
